let rowSequence = 0;

const rowKey = () => `mc-row-${Date.now()}-${rowSequence++}`;
const valueOrEmpty = (value) => value === null || value === undefined ? '' : value;

export const DISCIPLINES = {
  cs2: {
    label: 'Counter-Strike 2',
    unit: 'Карта',
    currentUnit: 'Текущая карта',
    liveScore: 'Раунды на текущей карте',
    seriesScore: 'Карты в серии',
    playerFields: [
      ['kills', 'K'], ['deaths', 'D'], ['assists', 'A'],
      ['adr', 'ADR'], ['hs', 'HS %'], ['rating', 'Rating'],
    ],
  },
  mlbb: {
    label: 'Mobile Legends: Bang Bang',
    unit: 'Игра',
    currentUnit: 'Текущая игра',
    liveScore: 'Убийства в текущей игре',
    seriesScore: 'Игры в серии',
    playerFields: [
      ['hero', 'Герой'], ['kills', 'K'], ['deaths', 'D'],
      ['assists', 'A'], ['gold', 'Золото'], ['rating', 'Rating'],
    ],
  },
};

export const emptyTeam = () => ({
  team_id: '', name: '', tag: '', side: 'NONE', score: 0, maps: 0,
});

export const emptyPlayer = (team = 0) => ({
  _key: rowKey(), player_id: '', steam_id: '', team_id: '', side: 'NONE',
  team, nick: '', hero: '', kills: 0, deaths: 0, assists: 0, gold: 0,
  damage: 0, headshots: 0, rounds_played: 0, kast: '', adr: 0, hs: 0, rating: '',
});

export const emptyMap = (discipline = 'cs2', index = 0) => ({
  _key: rowKey(), name: `${discipline === 'mlbb' ? 'Игра' : 'Карта'} ${index + 1}`,
  a: '', b: '', status: 'pending',
});

export function createEmptyMatch(discipline = 'cs2') {
  return {
    slug: '', tournament_key: '', discipline, title: '', stage: '',
    status: 'scheduled', best_of: 3, scheduled_at: '', stream_url: '',
    source_mode: 'manual', current_map: '', round: '', game_time: '',
    teams: [emptyTeam(), emptyTeam()],
    maps: Array.from({ length: 3 }, (_, index) => emptyMap(discipline, index)),
    players: [
      ...Array.from({ length: 5 }, () => emptyPlayer(0)),
      ...Array.from({ length: 5 }, () => emptyPlayer(1)),
    ],
    rounds: [],
    event_text: '',
  };
}

function toDateTimeLocal(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (number) => String(number).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

export function matchToDraft(match) {
  const discipline = DISCIPLINES[match?.discipline] ? match.discipline : 'cs2';
  const teams = [0, 1].map((index) => {
    const team = { ...emptyTeam(), ...(match?.teams?.[index] || {}) };
    return { ...team, score: valueOrEmpty(team.score), maps: valueOrEmpty(team.maps) };
  });
  const players = (match?.players || []).map((player) => {
    const normalized = { ...emptyPlayer(Number(player.team) === 1 ? 1 : 0), ...player, _key: rowKey() };
    ['player_id', 'steam_id', 'team_id', 'side', 'nick', 'hero', 'kills', 'deaths', 'assists',
      'gold', 'damage', 'headshots', 'rounds_played', 'kast', 'adr', 'hs', 'rating'].forEach((key) => {
      normalized[key] = valueOrEmpty(normalized[key]);
    });
    return normalized;
  });
  [0, 1].forEach((team) => {
    const missing = Math.max(0, 5 - players.filter((player) => player.team === team).length);
    players.push(...Array.from({ length: missing }, () => emptyPlayer(team)));
  });
  const maps = (match?.maps || []).map((map, index) => {
    const normalized = { ...emptyMap(discipline, index), ...map, _key: rowKey() };
    return { ...normalized, a: valueOrEmpty(normalized.a), b: valueOrEmpty(normalized.b) };
  });
  return {
    ...createEmptyMatch(discipline),
    slug: match?.slug || '',
    tournament_key: match?.tournament_key || '',
    discipline,
    title: match?.title || '',
    stage: match?.stage || '',
    status: match?.status || 'scheduled',
    best_of: match?.best_of || 3,
    scheduled_at: toDateTimeLocal(match?.scheduled_at),
    stream_url: match?.stream_url || '',
    source_mode: match?.source_mode || 'manual',
    current_map: match?.current_map || '',
    round: valueOrEmpty(match?.round),
    game_time: match?.game_time || '',
    teams,
    maps: maps.length ? maps : Array.from({ length: 3 }, (_, index) => emptyMap(discipline, index)),
    players,
    rounds: Array.isArray(match?.rounds) ? match.rounds : [],
    event_text: '',
  };
}

export function slugify(value) {
  return String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 90);
}

const numberValue = (value, fallback = 0) => {
  if (value === '' || value === null || value === undefined) return fallback;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const nullableNumber = (value) => value === '' || value === null || value === undefined
  ? null
  : numberValue(value, null);

export function buildMatchPayload(draft) {
  const discipline = DISCIPLINES[draft.discipline] ? draft.discipline : 'cs2';
  const teams = draft.teams.slice(0, 2).map((team, index) => ({
    team_id: String(team.team_id || `team-${index + 1}`).trim(), name: team.name.trim(),
    tag: team.tag.trim().toUpperCase().slice(0, 10),
    side: ['CT', 'T', 'SPECTATOR', 'NONE'].includes(team.side) ? team.side : 'NONE',
    score: numberValue(team.score),
    maps: numberValue(team.maps),
  }));
  const players = draft.players
    .filter((player) => player.nick.trim())
    .map((player) => {
      const common = {
        player_id: String(player.player_id || player.steam_id || `manual-${player.team}-${player.nick}`).trim(),
        steam_id: String(player.steam_id || '').trim() || null,
        team_id: String(player.team_id || teams[Number(player.team) === 1 ? 1 : 0].team_id).trim(),
        side: ['CT', 'T', 'SPECTATOR', 'NONE'].includes(player.side) ? player.side : 'NONE',
        team: Number(player.team) === 1 ? 1 : 0,
        nick: player.nick.trim(),
        kills: numberValue(player.kills), deaths: numberValue(player.deaths),
        assists: numberValue(player.assists), damage: numberValue(player.damage),
        headshots: numberValue(player.headshots), rounds_played: numberValue(player.rounds_played),
        kast: nullableNumber(player.kast), rating: nullableNumber(player.rating),
      };
      return discipline === 'mlbb'
        ? { ...common, hero: player.hero.trim(), gold: numberValue(player.gold) }
        : { ...common, adr: numberValue(player.adr), hs: numberValue(player.hs) };
    });
  const maps = draft.maps
    .filter((map) => map.name.trim())
    .map((map) => ({
      name: map.name.trim(), a: nullableNumber(map.a), b: nullableNumber(map.b),
      status: ['pending', 'live', 'finished'].includes(map.status) ? map.status : 'pending',
    }));
  const scheduled = draft.scheduled_at ? new Date(draft.scheduled_at) : null;
  const payload = {
    slug: draft.slug.trim(), tournament_key: draft.tournament_key.trim(), discipline,
    title: draft.title.trim() || `${teams[0].name} — ${teams[1].name}`,
    stage: draft.stage.trim(), status: draft.status, best_of: numberValue(draft.best_of, 3),
    scheduled_at: scheduled && !Number.isNaN(scheduled.getTime()) ? scheduled.toISOString() : null,
    stream_url: draft.stream_url.trim(), source_mode: 'manual',
    type: 'operator_update',
    snapshot: {
      current_map: draft.current_map.trim(),
      round: discipline === 'cs2' ? nullableNumber(draft.round) : null,
      game_time: discipline === 'mlbb' ? draft.game_time.trim() : '',
      teams, maps, players, rounds: Array.isArray(draft.rounds) ? draft.rounds : [],
    },
  };
  if (draft.event_text.trim()) payload.payload = { text: draft.event_text.trim() };
  return payload;
}

export function validateMatchDraft(draft) {
  const errors = [];
  if (!draft.tournament_key.trim()) errors.push('Укажите ID турнира');
  if (!draft.teams[0]?.name.trim() || !draft.teams[1]?.name.trim()) errors.push('Укажите обе команды');
  if (!draft.title.trim() && (!draft.teams[0]?.name.trim() || !draft.teams[1]?.name.trim())) errors.push('Укажите название матча');
  if (draft.slug && !/^[a-z0-9][a-z0-9-]{0,99}$/.test(draft.slug)) errors.push('Slug: только a-z, 0-9 и дефисы');
  return errors;
}
